/* istanbul ignore file */
import React from 'react';
import { Provider, useRollbar } from '@rollbar/react';
import type Rollbar from 'rollbar';

const env = import.meta.env || process.env;
const isDev = env?.VITE_ENV === 'local';

const rollbarConfig = {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  accessToken: env.VITE_ROLLBAR_CLIENT_TOKEN,
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  environment: env.VITE_ENV,
};

function getLocalContext() {
  return {
    url: window.location.href,
    impersonatingAccountId: localStorage.getItem('impersonatingAccountId'),
    companyName: localStorage.getItem('companyName'),
    hasUser: Boolean(localStorage.getItem('User')),
    hasToken: Boolean(localStorage.getItem('access_token')),
  };
}

interface BoundaryProps {
  children: React.ReactNode;
  rollbar: Rollbar;
}
interface BoundaryState {
  error: Error | null;
}

class TopLevelErrorBoundary extends React.Component<
  BoundaryProps,
  BoundaryState
> {
  constructor(props: BoundaryProps) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): BoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    const context = getLocalContext();
    const { rollbar } = this.props;
    if (isDev) {
      /* eslint-disable no-console */
      console.log('[ErrorBoundary] Caught error:', error.message);
      console.log('[ErrorBoundary] Stack:', error.stack);
      console.log('[ErrorBoundary] Component tree:', info.componentStack);
      console.log('[ErrorBoundary] Context:', context);
      /* eslint-enable no-console */
    }
    rollbar.error(error, { componentStack: info.componentStack, ...context });
  }

  render() {
    const { error } = this.state;
    const { children } = this.props;
    if (error) {
      return (
        <div style={{ padding: '2rem', fontFamily: 'sans-serif' }}>
          <h2>Something went wrong</h2>
          <p>
            Please refresh the page. If the problem persists, contact support.
          </p>
          <button type='button' onClick={() => window.location.reload()}>
            Refresh
          </button>
        </div>
      );
    }
    return children;
  }
}

function BoundaryWithRollbar({ children }: { children: React.ReactNode }) {
  const rollbar = useRollbar();
  return (
    <TopLevelErrorBoundary rollbar={rollbar}>{children}</TopLevelErrorBoundary>
  );
}

export default function ErrorReporter({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <Provider config={rollbarConfig}>
      <BoundaryWithRollbar>{children}</BoundaryWithRollbar>
    </Provider>
  );
}
